<template>
  <div class="grid">
    <div class="col-12">
      <div class="card">
        <SelectCompanies/>
        <ConfirmPopup>
          <template #message="slotProps">
            <div class="flex p-4">
              <i :class="slotProps.message.icon" style="font-size: 1.5rem"></i>
              <p class="pl-2">{{slotProps.message.message}}</p>
            </div>
          </template>
        </ConfirmPopup>

        <EditTools/>

        <div class="flex justify-content-between mb-4 table-label-search">
          <h3>Prices and Tools</h3>
          <div>
            <template v-if="this.selectedTools?.length">
              <Button @click="selectedPopular(true)" label="Popular" icon="pi pi-star-fill" class="mr-2 " />
              <Button @click="selectedPopular(false)" label="Unpopular" icon="pi pi-star" class="mr-2" />
              <Button @click="selectedHide(true)" label="Available" icon="pi pi-check" class="mr-2 p-button-danger" />
              <Button @click="selectedHide(false)" label="Unavailable" icon="pi pi-times" class="mr-2 p-button-danger" />
            </template>
            <Dropdown
                v-model="visibilityFilter" :options="visibilityOptions" optionLabel="label" optionValue="value"
                placeholder="Visibility" class="mr-2" style="height: 3rem" @change="onStatusFilter"
            />
            <Dropdown
                v-model="popularFilter" :options="popularOptions" optionLabel="label" optionValue="value"
                placeholder="Popular" class="mr-2" style="height: 3rem" @change="onStatusFilter"
            />
            <Button
                label="Add Tool"
                icon="pi pi-plus"
                class="p-button-success mr-2"
                style="height: 3rem"
                @click="openNew"
            />
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText v-model="search" placeholder="Keyword Search" @input="onSearch" />
            </span>
          </div>

        </div>
        <DataTable
          :value="getTools" :lazy="true" :paginator="true" :first="getPagination?.from-1"
          :rows="getPagination?.per_page ?? 25"  ref="dt" dataKey="id"
          :totalRecords="getPagination.total" :loading="loading" @page="onPage($event)"
          @sort="onSort($event)" :sortField="sort" :sortOrder="order"
          responsiveLayout="scroll" :rowsPerPageOptions="[10,25,50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          :globalFilterFields="['category']" v-model:filters="filters" filterDisplay="row" @filter="onFilter($event, ['category'])"
          v-model:selection="selectedTools" :selectAll="selectAll" @select-all-change="onSelectAllChange" @row-select="onRowSelect" @row-unselect="onRowUnselect"
        >
          <template #empty>
            Not found.
          </template>

          <Column selectionMode="multiple" headerStyle="width: 3em"></Column>

          <!-- API Name -->
          <Column field="api_provider_name" header="API" :sortable="true" header-style="min-width: 100px;" />

          <!-- Tool Name -->
          <Column field="name" header="Tool Name" :sortable="true"/>

          <!-- Category Name -->
          <Column field="category" header="Category" :showFilterMenu="false" :sortable="true">
            <template #body="{data}">
              <span class="image-text">{{data.category}}</span>
            </template>
            <template #filter="{filterModel,filterCallback}">
              <MultiSelect
                v-model="filterModel.value" @change="filterCallback()" :options="categories"
                optionLabel="name" optionValue="id" placeholder="Any" class="p-column-filter"
                :showClear="true"
              >
                <template #option="slotProps">
                  <div class="p-multiselect-representative-option">
                    <span class="image-text">{{slotProps.option.name}}</span>
                  </div>
                </template>
              </MultiSelect>
            </template>
          </Column>

          <!-- API Price ($) -->
          <Column field="price" header="API Price ($)" :sortable="true"/>

          <!-- Profit Price ($) -->
          <Column field="profit" header="Profit ($)" :sortable="true"/>

          <!-- Sell Price ($) -->
          <Column field="sell" header="Sell Price ($)" :sortable="true"/>

          <!-- Actions -->
          <Column headerStyle="min-width:16rem;">
            <template #body="slotProps">
              <Button
                v-if="slotProps.data.is_visible"
                v-tooltip="'Hide tool'"
                @click="hide(slotProps.data.id, false)"
                icon="pi pi-eye-slash" class="p-button-rounded p-button-danger mr-2"
              />
              <Button
                v-else
                v-tooltip="'Show tool'"
                @click="hide(slotProps.data.id, true)"
                icon="pi pi-eye" class="p-button-rounded p-button-danger mr-2"
              />

              <Button
                v-if="slotProps.data.is_popular"
                v-tooltip="'Make unpopular'"
                @click="popular(slotProps.data.id, false)"
                icon="pi pi-star-fill" class="p-button-rounded mr-2"
              />
              <Button
                v-else
                v-tooltip="'Make popular'"
                @click="popular(slotProps.data.id, true)"
                icon="pi pi-star" class="p-button-rounded mr-2"
              />

              <Button
                icon="pi pi-pencil" class="p-button-rounded p-button-warning mr-2"
                v-tooltip="'Edit'"
                @click="edit(slotProps.data.id)"
              />
              <Button
                icon="pi pi-check-circle" class="p-button-rounded p-button-success mr-2"
                v-tooltip="'Publish'"
                @click="handlePublish($event, slotProps.data.id)"
              />
            </template>
          </Column>

        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import store from "@store";
import toastParamBuilder from "@/builders/ToastParamBuilder";
import categoryResource from "@http/CategoryResource";
import {FilterMatchMode} from "primevue/api";
import EditTools from "@/components/tools/EditTools";
import SelectCompanies from "@/components/tools/SelectCompanies";
import toolResource from "@http/ToolResource";
import dataTable from "@/mixins/dataTable";

export default {
  mixins: [dataTable],

  components: {
    SelectCompanies,
    EditTools
  },

  data() {
    return {
      categories: [],
      selectedTools: null,
      selectAll: false,
      visibilityFilter: null,
      popularFilter: null,
      visibilityOptions: [
        {label: 'All visibility', value: null},
        {label: 'Visible', value: '1'},
        {label: 'Hidden', value: '0'},
      ],
      popularOptions: [
        {label: 'All', value: null},
        {label: 'Popular', value: '1'},
        {label: 'Unpopular', value: '0'},
      ],
      filters: {
        category: {value: null, matchMode: FilterMatchMode.EQUALS},
      },
    }
  },

  methods: {
    openNew() {
      this.$router.push({name: 'prices-and-tools-new'});
    },
    hide(id, is_visible) {
      toolResource.visibility(id, {is_visible})
        .then(() => {
          this.$router.push({
            name: this.$route.name,
            query: {
              ...this.$route.query,
              _r: Date.now(),
            }
          });
        })
        .catch(error => {
          this.$toast.add(toastParamBuilder.error(error.message ? error.message : error));
        });
    },
    popular(id, is_popular) {
      toolResource.popular(id, {is_popular})
        .then(() => {
          this.$router.push({
            name: this.$route.name,
            query: {
              ...this.$route.query,
              _r: Date.now(),
            }
          });
        })
        .catch(error => {
          this.$toast.add(toastParamBuilder.error(error.message ? error.message : error));
        });
    },
    selectedHide(is_visible) {
      let tool_ids = this.selectedTools?.map(item => item.id);
      toolResource.multipleVisible({tool_ids, is_visible})
        .then(() => {
          this.$router.push({
            name: this.$route.name,
            query: {
              ...this.$route.query,
              _r: Date.now(),
            }
          });
        })
        .catch(error => {
          this.$toast.add(toastParamBuilder.error(error.message ? error.message : error));
        });
    },
    selectedPopular(is_popular) {
      let tool_ids = this.selectedTools?.map(item => item.id);
      toolResource.multiplePopular({tool_ids ,is_popular})
        .then(() => {
          this.$router.push({
            name: this.$route.name,
            query: {
              ...this.$route.query,
              _r: Date.now(),
            }
          });
        })
        .catch(error => {
          this.$toast.add(toastParamBuilder.error(error.message ? error.message : error));
        });
    },
    edit(id) {
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          tool: id,
        }
      });
    },
    handlePublish(event, id) {
      this.$confirm.require({
        target: event.currentTarget,
        message: 'Publish to all companies or select?',
        acceptLabel: 'All',
        rejectLabel: 'Select',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          toolResource.publish(id, {})
            .then(() => {
              this.$toast.add(toastParamBuilder.success('Published'));
            });
        },
        reject: () => {
          this.$router.push({
            name: this.$route.name,
            query: {
              ...this.$route.query,
              select_companies: id,
            }
          });
        }
      });
    },
    onStatusFilter() {
      let query = Object.assign({}, this.$route.query);
      delete query.is_visible;
      delete query.is_popular;

      if (this.visibilityFilter !== null) {
        query.is_visible = this.visibilityFilter;
      }
      if (this.popularFilter !== null) {
        query.is_popular = this.popularFilter;
      }
      query.page = 1;

      this.$router.push({name: this.$route.name, query});
    },
    onSelectAllChange(event) {
      const selectAll = event.checked;
      if (selectAll) {
        this.selectAll = true;
        this.selectedTools = this.getTools;
      } else {
        this.selectAll = false;
        this.selectedTools = [];
      }
    },
    onRowSelect() {
      this.selectAll = this.selectedTools.length === this.getTools.length;
    },
    onRowUnselect() {
      this.selectAll = false;
    }
  },

  computed: {
    ...mapGetters({
      getTools: "tool/getTools",
      getPagination: "tool/getPagination",
    }),
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const categories = await categoryResource.categories();
      await store.dispatch('tool/pricesAndTools', to.query);
      next((vm) => {
        vm.loading = false;
        vm.categories = categories?.data;
        vm.visibilityFilter = vm.$route.query?.is_visible ?? null;
        vm.popularFilter = vm.$route.query?.is_popular ?? null;
        if (vm.$route.query?.category) {
          vm.filters.category.value = categories.data.filter((e) => {
            return vm.$route.query?.category.includes(e.id.toString());
          }).map(e => e.id);
        }
      });
    } catch (error) {
      next((vm) => {
        vm.$toast.add(toastParamBuilder.error(error.message ? error.message : error));
      });
    }
  },

  async beforeRouteUpdate(to) {
    await store.dispatch('tool/pricesAndTools', to.query)
      .finally(() => {
        this.loading = false;
      });
  },
}
</script>

<style lang="scss" scoped>
::v-deep(.p-paginator) {
  .p-paginator-current {
    margin-left: auto;
  }
}

::v-deep(.p-progressbar) {
  height: .5rem;
  background-color: #D8DADC;

  .p-progressbar-value {
    background-color: #607D8B;
  }
}

::v-deep(.p-datepicker) {
  min-width: 25rem;

  td {
    font-weight: 400;
  }
}

::v-deep(.p-datatable.p-datatable-customers) {
  .p-datatable-header {
    padding: 1rem;
    text-align: left;
    font-size: 1.5rem;
  }

  .p-paginator {
    padding: 1rem;
  }

  .p-datatable-thead > tr > th {
    text-align: left;
  }

  .p-datatable-tbody > tr > td {
    cursor: auto;
  }

  .p-dropdown-label:not(.p-placeholder) {
    text-transform: uppercase;
  }
}
</style>
